import clsx from 'clsx'
import { Key, ReactNode, useContext } from 'react'
import { formatDate, snakeToTitle } from '../utils'
import Button from './Button'
import './DataTable.css'
import { CheckIcon, ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon, CloseIcon } from './icons'
import { PreferencesContext } from './PreferencesContext'
import Skeleton from './Skeleton'

type DataTableResolver<T, R> = (args: {
    item: T
}) => R

type TableCellAlign = 'start' | 'end'

export interface DataTableCol<T> {
    key: string
    title?: ReactNode
    cell?: DataTableResolver<T, ReactNode>
    sortable?: boolean
    sortKey?: string
    minWidth?: number | string
    align?: TableCellAlign
    wrap?: boolean
}

export interface ColSort {
    sort: string
    direction: string
}

interface HeaderCellProps<T> {
    col: DataTableCol<T>
    columnSort?: ColSort
    onColumnSort?: (sort?: ColSort) => void
    align?: TableCellAlign
}

export function HeaderCell<T>({ col, columnSort, onColumnSort, align }: HeaderCellProps<T>) {
    const { key, title, sortable, sortKey } = col
    const sort = sortKey ?? key
    const handleSort = () => {
        if (columnSort?.sort !== sort) {
            onColumnSort?.({ sort, direction: 'asc' })
        } else if (columnSort?.direction === 'desc') {
            onColumnSort?.()
        } else {
            onColumnSort?.({ sort, direction: 'desc' })
        }
    }
    return <div className="table-header-cell">
        <div className={clsx('header-cell-content', align)}>
            <span>{title ?? snakeToTitle(key)}</span>
            {sortable && (
                <Button
                    size="tiny"
                    variant="secondary"
                    onClick={() => handleSort()}
                    icon={
                        columnSort?.sort === sort
                            ? columnSort?.direction === 'asc'
                                ? <ChevronUpIcon />
                                : <ChevronDownIcon />
                            : <ChevronUpDownIcon />
                    } />
            )}
        </div>
    </div>
}

export interface DataTableProps<T, C = Record<string, unknown>> {
    columns: Array<DataTableCol<T>>
    context?: C
    items?: T[]
    itemKey?: DataTableResolver<T, Key>
    emptyMessage?: ReactNode
    selectedRow?: Key
    onSelectRow?: (row: T) => void
    columnSort?: ColSort
    onColumnSort?: (sort?: ColSort) => void
    isLoading?: boolean
}

export function DataTable<T>({
    columns,
    emptyMessage = 'No Results',
    items,
    itemKey,
    selectedRow,
    onSelectRow,
    columnSort,
    onColumnSort,
    isLoading = false,
}: DataTableProps<T>) {
    const [preferences] = useContext(PreferencesContext)
    return (
        <div className="ui-table">
            <div className="table-header">
                {
                    columns.map(col => (
                        <HeaderCell<T>
                            key={col.key}
                            col={col}
                            onColumnSort={onColumnSort}
                            columnSort={columnSort}
                            align={col.align} />
                    ))
                }
            </div>
            {
                (items && items.length > 0)
                    ? items.map(item => {

                        const args = { item }
                        const key = itemKey ? itemKey(args) : (item as any).id

                        return (
                            <div
                                className={clsx(
                                    'table-row',
                                    onSelectRow ? ' table-row-interactive' : '',
                                    selectedRow === key ? ' table-row-selected' : '',
                                )}
                                key={key}
                                onClick={() => onSelectRow?.(item)}
                            >
                                {
                                    columns.map(col => {
                                        let value: any = col.cell
                                            ? col.cell(args)
                                            : item[col.key as keyof T]
                                        const wrap = col.wrap === undefined
                                        if (!col.cell) {
                                            if ((col.key.endsWith('_at') || col.key.endsWith('_until'))
                                                && (typeof value === 'string' || typeof value === 'number')) {
                                                value = formatDate(preferences, value, 'Pp')
                                            }
                                            if (typeof value === 'boolean') {
                                                value = value ? <CheckIcon /> : <CloseIcon />
                                            }
                                        }
                                        return (
                                            <div className={clsx('table-cell', col.align, !wrap && 'no-wrap')} key={col.key} style={col.minWidth ? { minWidth: col.minWidth } : {}}>
                                                {value ?? <>&#8211;</>}
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        )
                    })
                    : isLoading
                        ? Array.from({ length: 3 }, (x, i) => (
                            <div className="table-row loading" key={i}>
                                {
                                    columns.map(col => (
                                        <div className="table-cell" key={col.key}>
                                            <Skeleton />
                                        </div>
                                    ))
                                }
                            </div>
                        ))
                        : <div className="table-row">
                            <div className="table-cell">
                                {emptyMessage}
                            </div>
                        </div>
            }
        </div>
    )
}
